// File: webelieve-frontend/src/features/notes/components/NotesSplitView.tsx
// Master-detail split view shell with resizable left panel

import React, { useCallback, useRef, useEffect, useMemo, useState } from 'react';
import { useNotesStore } from '../stores/useNotesStore';
import NoteListPanel from './NoteListPanel';
import NoteEditor from './NoteEditor';
import type { Note } from '../types/note.types';

interface NotesSplitViewProps {
  clientId?: string;
  matterId?: string;
}

const MIN_WIDTH = 280;
const MAX_WIDTH = 500;
const COLLAPSED_WIDTH = 48;

const NotesSplitView: React.FC<NotesSplitViewProps> = ({ clientId, matterId }) => {
  const {
    notes,
    selectedNoteId,
    listPanelWidth,
    listPanelCollapsed,
    selectNote,
    setListPanelWidth,
    setListPanelCollapsed,
  } = useNotesStore();

  const [isCreating, setIsCreating] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  const preFocusListCollapsedRef = useRef<boolean | null>(null);

  // Pointer-based resize (MailClientShell pattern)
  const dragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (listPanelCollapsed || focusMode) return;
      dragging.current = true;
      startX.current = e.clientX;
      startWidth.current = listPanelWidth;
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [listPanelWidth, listPanelCollapsed, focusMode]
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging.current) return;
      const delta = e.clientX - startX.current;
      const next = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, startWidth.current + delta));
      setListPanelWidth(next);
    },
    [setListPanelWidth]
  );

  const onPointerUp = useCallback(() => {
    dragging.current = false;
  }, []);

  const handleFocusModeChange = useCallback((next: boolean) => {
    setFocusMode((current) => {
      if (current === next) return current;

      if (next) {
        preFocusListCollapsedRef.current = listPanelCollapsed;
        setListPanelCollapsed(true);
      } else {
        const restoreCollapsed = preFocusListCollapsedRef.current;
        if (restoreCollapsed !== null) {
          setListPanelCollapsed(restoreCollapsed);
        }
        preFocusListCollapsedRef.current = null;
      }

      return next;
    });
  }, [listPanelCollapsed, setListPanelCollapsed]);

  // Keyboard navigation
  useEffect(() => {
    const isInteractiveElement = (element: HTMLElement | null): boolean => {
      if (!element) return false;
      if (element.isContentEditable) return true;

      return Boolean(
        element.closest(
          'input, textarea, select, option, button, [contenteditable="true"], [role="textbox"], [role="combobox"], [role="listbox"]'
        )
      );
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && focusMode) {
        e.preventDefault();
        handleFocusModeChange(false);
        return;
      }

      // Ignore note-list shortcuts while user is interacting with form controls/editors.
      const target = e.target as HTMLElement | null;
      const activeElement = document.activeElement as HTMLElement | null;
      if (isInteractiveElement(target) || isInteractiveElement(activeElement)) return;

      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        const currentIndex = notes.findIndex((n) => n.note_id === selectedNoteId);
        if (e.key === 'ArrowUp' && currentIndex > 0) {
          selectNote(notes[currentIndex - 1].note_id);
        } else if (e.key === 'ArrowDown' && currentIndex < notes.length - 1) {
          selectNote(notes[currentIndex + 1].note_id);
        }
      } else if (e.key === 'Escape') {
        selectNote(null);
        setIsCreating(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [notes, selectedNoteId, selectNote, focusMode, handleFocusModeChange]);

  // Find selected note
  const selectedNote: Note | null = useMemo(() => {
    if (!selectedNoteId) return null;
    return notes.find((n) => n.note_id === selectedNoteId) || null;
  }, [notes, selectedNoteId]);

  // Clear isCreating when the newly-created note appears in the list
  useEffect(() => {
    if (isCreating && selectedNote) {
      setIsCreating(false);
    }
  }, [isCreating, selectedNote]);

  const handleCreateNote = () => {
    selectNote(null);
    setIsCreating(true);
  };

  useEffect(() => {
    if (!selectedNoteId && !isCreating && focusMode) {
      handleFocusModeChange(false);
    }
  }, [selectedNoteId, isCreating, focusMode, handleFocusModeChange]);

  // Determine what the editor should show
  const editorNote = isCreating ? null : selectedNote;
  const isListHidden = focusMode;

  return (
    <div className="flex h-full w-full overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
      {/* Left panel — list */}
      <div
        className={`shrink-0 overflow-hidden flex flex-col transition-[width] duration-200 ${isListHidden ? 'border-r-0' : 'border-r border-gray-200 dark:border-gray-700'}`}
        style={{ width: isListHidden ? 0 : (listPanelCollapsed ? COLLAPSED_WIDTH : listPanelWidth) }}
      >
        {!isListHidden && listPanelCollapsed ? (
          // Collapsed rail
          <div className="flex flex-col items-center py-3 gap-3">
            <button
              onClick={() => setListPanelCollapsed(false)}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
              title="Expand notes list"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <button
              onClick={handleCreateNote}
              className="p-2 rounded-lg bg-lex-accent hover:bg-lex-accent-hover text-lex-accent-contrast"
              title="New Note"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
            {/* Note count indicator */}
            <span className="text-[10px] text-gray-400 writing-mode-vertical">
              {notes.length}
            </span>
          </div>
        ) : !isListHidden ? (
          <NoteListPanel
            clientId={clientId}
            matterId={matterId}
            onCreateNote={handleCreateNote}
          />
        ) : null}
      </div>

      {/* Resize handle */}
      {!listPanelCollapsed && !isListHidden && (
        <div
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          className="w-1 cursor-col-resize bg-gray-100 dark:bg-gray-700 hover:bg-lex-accent/40 dark:hover:bg-lex-accent/40 transition-colors shrink-0"
        />
      )}

      {/* Collapse/expand toggle at panel edge */}
      {!listPanelCollapsed && !isListHidden && (
        <button
          onClick={() => setListPanelCollapsed(true)}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-0.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-r shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 hidden group-hover:block"
          style={{ marginLeft: listPanelWidth }}
          title="Collapse notes list"
        >
          <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      {/* Right panel — editor */}
      <div className="flex-1 flex min-h-0 overflow-hidden">
        <NoteEditor
          note={editorNote}
          clientId={clientId}
          matterId={matterId}
          focusMode={focusMode}
          onSetFocusMode={handleFocusModeChange}
        />
      </div>
    </div>
  );
};

export default NotesSplitView;
